import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class RedeemService {

  constructor() { }

  /**
   * Converts a date to "Month Day, Year" format (e.g., "December 3, 2024")
   * Used for UI Display
   */
  formatToLongDate(dateInput: string | Date | number | null): string {
    if (!dateInput){
       return 'N/A';
      }
    const date = new Date(dateInput);
    if (isNaN(date.getTime())){
      return 'Invalid Date';
    }

    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  /**
   * Converts a Date object to "YYYY-MM-DD" string.
   * Used for API Payloads
   */
  formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Maps the raw array from the API response to the component's state structure.
   */
  mapApiResultToState(data: any[]) {
    // Default State Structure
    const result = {
      buyload: 0,
      pins: 0,
      stores: 0,
      paybills: 0,
      preSetRates: {
        buyload: { rate: null, date: null, isSet: false },
        pins: { rate: null, date: null, isSet: false },
        stores: { rate: null, date: null, isSet: false },
        paybills: { rate: null, date: null, isSet: false }
      }
    };

    if (!Array.isArray(data)) {
      return result;
    }

    data.forEach((item: any) => {
      const type = item.redeemType?.toUpperCase();
      let category: 'buyload' | 'pins' | 'stores' | 'paybills' | null = null;

      switch (type) {
        case 'BUYLOAD': category = 'buyload'; break;
        case 'PINS': category = 'pins'; break;
        case 'STORES': category = 'stores'; break;
        case 'PAYBILLS': category = 'paybills'; break;
      }

      if (category) {
        // Map Active Rate
        result[category] = item.redeemPercentage ?? 0;

        // Map Pre-set Rate
        const hasPreset = item.preSetPercentage !== null && item.preSetPercentage !== undefined;
        if (hasPreset) {
          result.preSetRates[category] = {
            rate: item.preSetPercentage,
            date: item.effectivityDate,
            isSet: true
          };
        }
      }
    });

    return result;
  }

  /**
   * Constructs the payload for updating the active rate.
   */
  createUpdatePayload(category: string, value: number) {
    return {
      redeemPercentageParams: [
        {
          redeemType: category.toUpperCase(),
          redeemPercentage: value
        }
      ]
    };
  }

  /**
   * Constructs the payload for saving a pre-set rate.
   */
  createPreSetPayload(category: string, rate: number, date: Date) {
    const formattedDate = this.formatDateForApi(date);
    return {
      redeemPercentageParams: [
        {
          redeemType: category.toUpperCase(),
          effectivityDate: formattedDate,
          preSetPercentage: rate,
          isDisabled: false
        }
      ]
    };
  }

  /**
   * Constructs the payload for clearing a pre-set rate.
   */
  createClearPreSetPayload(category: string) {
    return {
      redeemPercentageParams: [
        {
          redeemType: category.toUpperCase(),
          preSetPercentage: null,
          effectivityDate: null
        }
      ]
    };
  }

  /**
   * Checks if the preset form has unsaved changes
   */
  hasUnsavedPresetChanges(
    formValue: { rate: number | null, date: Date | null },
    originalData: { rate: number | null, date: string | Date | null, isSet: boolean }
  ): boolean {
    // If nothing is filled in the form, no changes
    if (formValue.rate === null && formValue.date === null) {
      return false;
    }

    // If form has any value, it's considered a change
    if (formValue.rate !== null || formValue.date !== null) {
      // Compare with original data
      if (!originalData.isSet) {
        // If there was no preset before, any input is a change
        return true;
      }

      // Compare rate
      if (formValue.rate !== originalData.rate) {
        return true;
      }

      // Compare date
      if (formValue.date) {
        const formDate = this.formatDateForApi(formValue.date);
        const originalDate = originalData.date ? this.formatDateForApi(new Date(originalData.date)) : null;
        if (formDate !== originalDate) {
          return true;
        }
      } else if (originalData.date !== null) {
        return true;
      }
    }

    return false;
  }
}